import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import DataTable from "../components/DataTable";
import { fetchPatients } from "../services/api";

const TableControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SearchBar = styled.div`
  flex: 1;
  min-width: 250px;

  input {
    width: 100%;
    padding: 0.75rem;
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color);
    background-color: var(--card-bg);
    color: var(--text-primary);
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.2);
    }

    &::placeholder {
      color: var(--text-secondary);
    }
  }
`;

const SortControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  label {
    font-size: 0.875rem;
    color: var(--text-secondary);
    font-weight: 500;
  }

  select {
    padding: 0.5rem;
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color);
    background-color: var(--card-bg);
    color: var(--text-primary);
    font-size: 0.875rem;
    min-width: 200px;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
    }

    option {
      background-color: var(--card-bg);
      color: var(--text-primary);
    }
  }
`;


const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1.25rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--card-highlight-bg);
  border-radius: 0 0 var(--border-radius) var(--border-radius);

  .pagination-info {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background-color: var(--card-bg);
    padding: 0.75rem 1rem;
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    min-width: 200px;
    text-align: center;
  }

  .pagination-controls {
    display: flex;
    gap: 0.75rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;

    .pagination-info {
      width: 100%;
      text-align: center;
    }

    .pagination-controls {
      width: 100%;
      justify-content: center;
    }
  }
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: ${(props) =>
    props.active ? "var(--primary-color)" : "var(--card-bg)"};
  color: ${(props) => (props.active ? "white" : "var(--text-primary)")};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  font-size: ${(props) => (props.isNavigation ? "1.5rem" : "0.95rem")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.6" : "1")};
  transition: all 0.2s ease;
  box-shadow: ${(props) =>
    props.active
      ? "0 2px 4px rgba(0, 0, 0, 0.2)"
      : "0 1px 3px rgba(0, 0, 0, 0.05)"};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.active ? "var(--primary-dark)" : "var(--card-highlight-bg)"};
    border-color: ${(props) =>
      props.active ? "var(--primary-dark)" : "var(--primary-color)"};
    color: ${(props) => (props.active ? "white" : "var(--primary-color)")};
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    background-color: var(--card-highlight-bg);
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  background-color: var(--card-highlight-bg);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-left: 0.5rem;

  &:hover {
    text-decoration: underline;
    background: none;
  }
`;

const AddressesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const AddressCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 0.75rem;
  background-color: var(--card-bg);

  .address-line {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }

  .city-state-zip {
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-color);
    border-top-color: var(--primary-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const PatientActionButton = styled(Link)`
  display: inline-block;
  padding: 0.5rem 0.75rem;
  background-color: var(--primary-color);
  color: white;
  border-radius: var(--border-radius);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--primary-dark);
  }
`;

function PatientsPage() {
  const [patientsData, setPatientsData] = useState({
    patients: [],
    count: 0,
    next: null,
    previous: null,
    currentPage: 1
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);

  const pageSize = 10; // Items per page

  useEffect(() => {
    const getPatients = async () => {
      try {
        setLoading(true);
        const data = await fetchPatients({
          page,
          search: searchTerm,
          sortBy,
          sortOrder
        });
        setPatientsData(data);
        setError(null);
      } catch (err) {
        setError("Failed to load patients. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search input to avoid excessive API calls
    const timer = setTimeout(() => {
      getPatients();
    }, 300);

    return () => clearTimeout(timer);
  }, [page, searchTerm, sortBy, sortOrder]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setPage(1); // Reset to first page on sort change
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Format date of birth to MM/DD/YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  // Calculate total pages
  const totalPages = Math.ceil(patientsData.count / pageSize);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages are less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate which pages to show
      if (page <= 3) {
        // Show first 3 pages, ellipsis, and last page
        for (let i = 1; i <= 3; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        // Show first page, ellipsis, and last 3 pages
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page, ellipsis, current page and neighbors, ellipsis, last page
        pages.push(1);
        pages.push("...");
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const columns = [
    {
      header: "Name",
      accessor: "first_name",
      render: (row) => (
        <Link
          to={`/patients/${row.patient_id}`}
          style={{ color: "var(--primary-color)", fontWeight: 500 }}
        >
          {row.first_name} {row.last_name}
        </Link>
      )
    },
    {
      header: "Email",
      accessor: "email"
    },
    {
      header: "Phone",
      accessor: "phone"
    },
    {
      header: "Date of Birth",
      accessor: "date_of_birth",
      render: (row) => formatDate(row.date_of_birth)
    },
    {
      header: "Gender",
      accessor: "gender"
    },
    {
      header: "Addresses",
      accessor: "addresses",
      render: (row) => (
        <div>
          {row.addresses.length > 0 ? (
            <div>
              <div>
                {row.addresses.slice(0, 1).map((address) => (
                  <span key={address.address_id}>
                    {address.city}, {address.state.state_abbreviation}
                  </span>
                ))}
                {row.addresses.length > 1 && (
                  <span> +{row.addresses.length - 1} more </span>
                )}
                <ExpandButton onClick={() => toggleRowExpanded(row.patient_id)}>
                  {expandedRows[row.patient_id] ? "Hide" : "View"}
                </ExpandButton>
              </div>

              {expandedRows[row.patient_id] && (
                <AddressesGrid>
                  {row.addresses.map((address) => (
                    <AddressCard key={address.address_id}>
                      <div className="address-line">{address.address1}</div>
                      {address.address2 && (
                        <div className="address-line">{address.address2}</div>
                      )}
                      <div className="city-state-zip">
                        {address.city}, {address.state.state_abbreviation}{" "}
                        {address.zipcode}
                      </div>
                    </AddressCard>
                  ))}
                </AddressesGrid>
              )}
            </div>
          ) : (
            <span>No addresses available</span>
          )}
        </div>
      )
    },
    {
      header: "Status",
      accessor: "prognosis_match",
      render: (row) => (
        <Badge
          style={{
            backgroundColor:
              row.prognosis_match === "True"
                ? "var(--success-bg)"
                : row.prognosis_match === "False"
                ? "var(--card-highlight-bg)"
                : "var(--error-bg)",
            color:
              row.prognosis_match === "True"
                ? "var(--success-color)"
                : row.prognosis_match === "False"
                ? "var(--text-secondary)"
                : "var(--error-color)"
          }}
        >
          {row.prognosis_match === "True"
            ? "Matched"
            : row.prognosis_match === "False"
            ? "Unmatched"
            : "API Failed"}
        </Badge>
      )
    },
    {
      header: "Actions",
      accessor: "patient_id",
      render: (row) => (
        <PatientActionButton to={`/patients/${row.patient_id}`}>
          View Details
        </PatientActionButton>
      )
    }
  ];

  return (
    <div>
      <PageHeader
        title="Patients"
        description="Browse all patients in the database."
      />

      <Card>
        <TableControls>
          <SearchBar>
            <input
              type="text"
              placeholder="Search by name, email, phone, or DOB (YYYY-MM-DD)..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </SearchBar>

          <SortControls>
            <label htmlFor="sort-by">Sort by:</label>
            <select id="sort-by" value={sortBy} onChange={handleSortChange}>
              <option value="created_at">Date Added</option>
              <option value="last_name">Last Name</option>
              <option value="first_name">First Name</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="date_of_birth">Date of Birth</option>
              <option value="prognosis_match">Match Status</option>
            </select>

            <select
              value={sortOrder}
              onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
            >
              {sortBy === "created_at" ? (
                <>
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </>
              ) : (
                <>
                  <option value="asc">A → Z</option>
                  <option value="desc">Z → A</option>
                </>
              )}
            </select>
          </SortControls>
        </TableControls>

        {loading ? (
          <LoadingSpinner>
            <div className="spinner"></div>
          </LoadingSpinner>
        ) : error ? (
          <p
            style={{
              color: "var(--error-color)",
              textAlign: "center",
              padding: "2rem"
            }}
          >
            {error}
          </p>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={patientsData.patients}
              emptyMessage="No patients found matching your search criteria."
            />

            {totalPages > 1 && (
              <Pagination>
                <div className="pagination-info">
                  Showing{" "}
                  {Math.min(pageSize * (page - 1) + 1, patientsData.count)} to{" "}
                  {Math.min(pageSize * page, patientsData.count)} of{" "}
                  {patientsData.count} patients
                </div>

                <div className="pagination-controls">
                  <PaginationButton
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1}
                    isNavigation
                  >
                    «
                  </PaginationButton>

                  <PaginationButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    isNavigation
                  >
                    ‹
                  </PaginationButton>

                  {getPageNumbers().map((pageNum, index) =>
                    pageNum === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        style={{
                          padding: "0.5rem",
                          color: "var(--text-secondary)"
                        }}
                      >
                        ...
                      </span>
                    ) : (
                      <PaginationButton
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        active={page === pageNum}
                      >
                        {pageNum}
                      </PaginationButton>
                    )
                  )}

                  <PaginationButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages || totalPages === 0}
                    isNavigation
                  >
                    ›
                  </PaginationButton>

                  <PaginationButton
                    onClick={() => handlePageChange(totalPages)}
                    disabled={page === totalPages || totalPages === 0}
                    isNavigation
                  >
                    »
                  </PaginationButton>
                </div>
              </Pagination>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default PatientsPage;
